import React from 'react';

const FIELDS = [
  { key: 'pest365', label: 'pest365' },
  { key: 'pest365Plus', label: 'pest365+' },
  { key: 'oneTime', label: 'oneTime' },
  { key: 'initialServiceVisit', label: 'ISV' },
  { key: 'mosquitoTickControl', label: 'MTC' },
];

// The analytics engine stores correlation pairs as `f1_f2` where f1 comes
// before f2 in the PRICE_FIELDS order. This helper finds the correct key.
const FIELD_ORDER = ['pest365', 'pest365Plus', 'oneTime', 'initialServiceVisit', 'mosquitoTickControl'];
function getPairKey(key1, key2) {
  if (key1 === key2) return null;
  const idx1 = FIELD_ORDER.indexOf(key1);
  const idx2 = FIELD_ORDER.indexOf(key2);
  if (idx1 < 0 || idx2 < 0) return null;
  return idx1 < idx2 ? `${key1}_${key2}` : `${key2}_${key1}`;
}

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '16px',
  },
  gridContainer: {
    display: 'inline-grid',
    gridTemplateColumns: `80px repeat(${FIELDS.length}, 64px)`,
    gridTemplateRows: `32px repeat(${FIELDS.length}, 64px)`,
    gap: '2px',
  },
  headerCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
  },
  rowLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingRight: '8px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
  },
  cell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: 600,
    transition: 'transform 0.15s ease',
  },
};

function getCellColor(r, isDiagonal) {
  if (isDiagonal) return { background: '#f3f4f6', color: '#9ca3af' };
  if (r == null) return { background: '#f9fafb', color: '#d1d5db' };

  const absR = Math.abs(r);
  if (absR > 0.8) return { background: '#1e40af', color: '#fff' };
  if (absR > 0.6) return { background: '#3b82f6', color: '#fff' };
  if (absR > 0.4) return { background: '#93c5fd', color: '#1e3a5f' };
  return { background: '#e5e7eb', color: '#6b7280' };
}

export default function CorrelationMatrix({ matrix }) {
  if (!matrix) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Correlation Matrix</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No correlation data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Correlation Matrix</div>
      <div style={{ overflowX: 'auto' }}>
        <div style={styles.gridContainer}>
          {/* Top-left empty cell */}
          <div />

          {/* Column headers */}
          {FIELDS.map((f) => (
            <div key={`col-${f.key}`} style={styles.headerCell}>
              {f.label}
            </div>
          ))}

          {/* Rows */}
          {FIELDS.map((rowField, ri) => (
            <React.Fragment key={`row-${rowField.key}`}>
              <div style={styles.rowLabel}>{rowField.label}</div>
              {FIELDS.map((colField, ci) => {
                const isDiagonal = ri === ci;
                const pairKey = getPairKey(rowField.key, colField.key);
                const r = isDiagonal ? 1.0 : (pairKey && matrix[pairKey] != null ? matrix[pairKey] : null);
                const colors = getCellColor(r, isDiagonal);

                return (
                  <div
                    key={`cell-${ri}-${ci}`}
                    style={{
                      ...styles.cell,
                      background: colors.background,
                      color: colors.color,
                    }}
                    title={
                      isDiagonal
                        ? `${rowField.label} (self)`
                        : r != null
                        ? `${rowField.label} vs ${colField.label}: r=${r.toFixed(3)}`
                        : `${rowField.label} vs ${colField.label}: N/A`
                    }
                  >
                    {r != null ? r.toFixed(2) : '--'}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div style={{ marginTop: '12px', display: 'flex', gap: '16px', fontSize: '11px', color: '#9ca3af' }}>
        <span>
          <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '2px', background: '#1e40af', verticalAlign: 'middle', marginRight: '4px' }} />
          r &gt; 0.8
        </span>
        <span>
          <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '2px', background: '#3b82f6', verticalAlign: 'middle', marginRight: '4px' }} />
          r &gt; 0.6
        </span>
        <span>
          <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '2px', background: '#93c5fd', verticalAlign: 'middle', marginRight: '4px' }} />
          r &gt; 0.4
        </span>
        <span>
          <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '2px', background: '#e5e7eb', verticalAlign: 'middle', marginRight: '4px' }} />
          r &lt; 0.4
        </span>
      </div>
    </div>
  );
}
